// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  refreshHighchartsSize,
  SubVisualization,
  VisualizationContentProps,
  VisualizationContentPropsTransformer,
} from '@/hybrid/annotation/ckEditorPlugins/components/content.utilities';
import { TableBuilderPropsOnly } from '@/hybrid/tableBuilder/TableBuilderPropsOnly.organism';
import { bindingsDefinition, injected, prop } from '@/hybrid/core/bindings.util';
import { sqContentApi } from '@/sdk';
import { useInjectedBindings } from '@/hybrid/core/hooks/useInjectedBindings.hook';
import _ from 'lodash';
import { XyPlotContent } from '@/hybrid/scatterPlot/XyPlotContent.organism';

import { ErrorBoundaryWithLogging } from '@/hybrid/core/ErrorBoundary.atom';
import { ReportContentService } from '@/hybrid/annotation/reportContent.service';
import { ErrorFallbackMinimal } from '@/hybrid/core/ErrorFallback.organism';
import { Visualization } from '@/hybrid/annotation/ckEditorPlugins/components/content.utilities.constants';
import { ChartViewContent } from '@/hybrid/reportEditor/ChartViewContent.molecule';
import { CONTENT_LOADING_CLASS, IMAGE_BORDER_CLASS } from '@/reportEditor/report.constants';
import { sqReportStore } from '@/core/core.stores';
import { EmptyContent } from '@/hybrid/reportEditor/EmptyContent.molecule';

const getAdaptedHeight = (
  visualization: Visualization,
  subVisualization: SubVisualization,
  height: number,
  parentElement: HTMLElement,
) => {
  if (!parentElement?.children[0]?.children) {
    return;
  }
  const siblings = _.slice(parentElement.children[0].children, 1);
  const totalHeight = _.reduce(
    siblings,
    (totalSoFar: number, element: HTMLElement) => totalSoFar + $(element).outerHeight(true),
    0,
  );
  return height - totalHeight;
};

const renderComponent: (
  visualization: Visualization,
  props: any,
  stopUpdate: boolean,
  loading: boolean,
) => JSX.Element = (visualization: Visualization, props: any, stopUpdate: boolean, loading: boolean) => {
  const InnerComponent =
    {
      [Visualization.TABLE]: TableBuilderPropsOnly,
      [Visualization.XY_PLOT]: XyPlotContent,
      [Visualization.TREND]: ChartViewContent,
      [Visualization.NONE]: EmptyContent,
    }[visualization] || null;
  if (_.isNull(InnerComponent)) {
    throw new Error('No visualization type found in React JSON blob');
  }
  return (
    <div
      data-testid={`innerContainer-${props.contentId}`}
      className={classNames('inheritMinHeight flexRowContainer reactJsonContent', {
        contentWrapperStopUpdate: stopUpdate,
        tableVisualization: visualization === Visualization.TABLE && !props.showChartView,
      })}>
      <InnerComponent {...props} stopUpdate={stopUpdate} />
    </div>
  );
};

const reactJsonContentBindings = bindingsDefinition({
  contentId: prop<string>(),
  style: prop<any>(),
  loading: prop<boolean>(),
  target: prop<any>(),
  src: prop<string>(),
  border: prop.optional<boolean>(),
  onLoad: prop<(properties: { visualization: Visualization }) => void>(),
  onError: prop<(error: string, errorCode: number) => void>(),
  onMeasurementsReady: prop<(width: number, height: number) => void>(),
  showMenu: prop.optional<() => void>(),
  error: prop.optional<string>(),
  errorClass: prop.optional<string>(),
  propertyOverrides: prop.optional<any>(),
  stopUpdate: prop.optional<boolean>(),
  height: prop.optional<number>(),
  width: prop.optional<number>(),
  sqReportContent: injected<ReportContentService>(),
});

/**
 * Handles React JSON visualizations. The properties state object is expected to the props to a props only version of a
 * Analysis visualization.
 */
export const ReactJsonContent: SeeqComponent<typeof reactJsonContentBindings> = ({
  contentId,
  errorClass,
  style,
  loading,
  target,
  src,
  onLoad,
  onError,
  border,
  onMeasurementsReady,
  showMenu = () => null,
  error,
  propertyOverrides,
  stopUpdate = true,
  height = 0,
  width = 0,
}) => {
  const { sqReportContent } = useInjectedBindings(reactJsonContentBindings);

  const [properties, setProperties] = useState<any | undefined>();
  const isCurrentlyLoading = useRef(false);

  const updateHighchartContent = () => {
    if (!properties) {
      return;
    }
    refreshHighchartsSize(contentId, {
      visualization: properties.visualization,
      subVisualization: properties.subVisualization,
      height: getAdaptedHeight(
        properties.visualization,
        properties.subVisualization,
        height ? height : target.current.clientHeight,
        target.current,
      ),
      width: null,
    });
  };

  useEffect(() => {
    if (isCurrentlyLoading.current) {
      /**
       * at this point there's an ongoing getReactBlob call, so we shouldn't do a new one
       * this is usually the case when the browser doesn't have enough time between auto-updates
       * to re-render all the interactive content elements
       * we should also show some kind of warning that there's a lot of contents so we suggest increasing
       * the auto-update interval
       */
      return;
    }
    const queryParams = new URLSearchParams(src.substr(src.indexOf('?')));
    const params = {
      hash: queryParams.get('hash'),
    };
    isCurrentlyLoading.current = true;
    sqContentApi
      .getReactBlob({ id: contentId }, { params, useManualAsync: true })
      .then(({ data }) => {
        const untypedProperties = data as any;
        if (untypedProperties.visualization) {
          const propertiesForVisualizationType = VisualizationContentPropsTransformer[untypedProperties.visualization]({
            ...untypedProperties,
            isContent: true,
            contentId,
            ...VisualizationContentProps[untypedProperties.visualization],
          });
          setProperties(propertiesForVisualizationType);
          onLoad(propertiesForVisualizationType);
        }
      })
      .catch((error) => {
        onError(error.data?.statusMessage, error.status);
      })
      .finally(() => {
        isCurrentlyLoading.current = false;
      });
  }, [src]);

  const measurementsCallback = useCallback(() => {
    const content = sqReportStore.getContentById(contentId);
    if (properties.visualization === Visualization.TABLE && !properties.showChartView) {
      onMeasurementsReady(0, 0);
    } else {
      onMeasurementsReady(content?.width ?? target.current.clientWidth, content?.height ?? target.current.clientHeight);
    }
  }, [properties, contentId]);

  useEffect(() => {
    if (!loading) {
      updateHighchartContent();
    }
  }, [width, height, properties, loading]);

  const getStyle = (errorClass: string) => {
    const width = errorClass && errorClass !== CONTENT_LOADING_CLASS.NO_CAPSULE_ERROR ? 'fit-content' : 'inherit';

    const styleOverrides: any = {
      maxHeight: undefined,
      maxWidth: undefined,
      width,
      height: 'inherit',
    };

    return {
      ...style,
      ...styleOverrides,
    };
  };

  const retry = () => sqReportContent.forceRefreshContent(contentId);

  return (
    <div
      data-testid={`content-${contentId}`}
      data-seeq-content={contentId}
      data-visualization={properties?.visualization ?? 'none'}
      data-sub-visualization={properties?.subVisualization ?? 'none'}
      style={getStyle(errorClass)}
      onClick={showMenu}
      ref={target}
      title={error}
      className={classNames(
        'inheritMinHeight',
        {
          [CONTENT_LOADING_CLASS.STOP_UPDATE]: stopUpdate,
          [IMAGE_BORDER_CLASS]: border,
        },
        errorClass ?? '',
        {
          [CONTENT_LOADING_CLASS.SPINNER]: loading && !errorClass,
          [CONTENT_LOADING_CLASS.LOADED]: !loading && !errorClass,
        },
      )}>
      {!errorClass && properties && (
        <ErrorBoundaryWithLogging fallback={(error) => <ErrorFallbackMinimal error={error} retry={retry} />}>
          {renderComponent(
            properties.visualization,
            {
              ...properties,
              ...propertyOverrides,
              onContentLoad: measurementsCallback,
              afterChartUpdate: measurementsCallback,
            },
            stopUpdate,
            loading,
          )}
        </ErrorBoundaryWithLogging>
      )}
    </div>
  );
};
